import { useState } from 'react';
import { initialSidebarOpen } from '@/hooks/use-sidebar-state';
import { cn } from '@/lib/utils';

/**
 * The HyperTek prototype's cold-load skeleton (h-prototype `components/layout/app-shell-skeleton.tsx`), shown
 * while the session read is in flight so the first paint already has the shell's silhouette: the dark sidebar
 * with its logo tile and row placeholders, and a content placeholder beside it.
 *
 * Product deltas:
 *   - The sidebar silhouette and the content offset follow the width the shell will open at (56 px collapsed
 *     unless this desktop's person left it expanded at 216 px), so nothing jumps sideways when the shell
 *     replaces it.
 *   - It keeps the `page-loading` line, visually hidden: that class is the hook the responsive suite waits on
 *     before it measures a screen, and the words are what a screen reader announces.
 *   - `role="status"`: the prototype puts `aria-label` on a bare `div`, which ARIA does not allow to be named.
 */
export function AppShellSkeleton() {
  const [open] = useState(initialSidebarOpen);
  return (
    <div role="status" aria-busy="true" aria-label="Đang tải" className="min-h-screen bg-[hsl(var(--h-background))]">
      <p className="page-loading sr-only">Đang tải…</p>

      {/* Sidebar silhouette (lg+ only): 56 px of icon tiles, 216 px with the label bars when it opens expanded. */}
      <div
        className={cn(
          'fixed left-0 top-0 hidden h-screen flex-col gap-0.5 bg-[hsl(var(--h-rail-bg))] px-2 lg:flex',
          open ? 'w-[216px]' : 'w-14',
        )}
      >
        <div className="flex h-13 shrink-0 items-center">
          <div className="size-10 shrink-0 animate-pulse rounded-[10px] bg-white/90" />
        </div>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex h-10 shrink-0 items-center gap-1">
            <div className="size-10 shrink-0 animate-pulse rounded-[10px] bg-white/10" />
            {open && <div className="h-3 w-24 animate-pulse rounded bg-white/10" />}
          </div>
        ))}
      </div>

      <div className={cn('min-h-screen p-4', open ? 'lg:pl-[216px]' : 'lg:pl-14')}>
        <div className="lg:p-6">
          <div className="mb-6 h-8 w-48 animate-pulse rounded bg-[hsl(var(--h-muted))]" />
          <div className="h-64 w-full animate-pulse rounded-[var(--h-radius-lg)] bg-[hsl(var(--h-muted))]" />
        </div>
      </div>
    </div>
  );
}
